import React, { useState, useEffect } from 'react';
import { emailApi, pitchesApi, googleApi } from '../utils/api';
import { useToast } from '../hooks/useToast';

function getCurrentWeekKey() {
  const now = new Date();
  const startOfYear = new Date(now.getFullYear(), 0, 1);
  const week = Math.ceil(((now - startOfYear) / 86400000 + startOfYear.getDay() + 1) / 7);
  return `${now.getFullYear()}-W${week}`;
}

// ─── Send Podcasts Modal ──────────────────────────────────────────────────────
function SendPodcastsModal({ clientData, onClose, onDraftCreated }) {
  const toast = useToast();
  const { client, savedPool, flaggedThisWeek } = clientData;
  const weekKey = getCurrentWeekKey();

  const [selected, setSelected] = useState(flaggedThisWeek.map(p => p.id));
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [swapWarning, setSwapWarning] = useState(null);
  const [googleAccounts, setGoogleAccounts] = useState([]);
  const [sendFromEmail, setSendFromEmail] = useState(client.googleAccountEmail || '');

  useEffect(() => {
    googleApi.getStatus().then(r => {
      const accounts = r.data.accounts || [];
      setGoogleAccounts(accounts);
      if (!sendFromEmail && accounts.length > 0) setSendFromEmail(client.googleAccountEmail || accounts[0].email || '');
    });
  }, []);

  // Sort pool: flagged this week → flagged other weeks → unflagged
  const sortedPool = [...savedPool].sort((a, b) => {
    const aThis = a.flaggedWeek === weekKey;
    const bThis = b.flaggedWeek === weekKey;
    if (aThis && !bThis) return -1;
    if (!aThis && bThis) return 1;
    if (a.flaggedWeek && !b.flaggedWeek) return -1;
    if (!a.flaggedWeek && b.flaggedWeek) return 1;
    return 0;
  });

  const toggleSelect = (pitch) => {
    const isSelected = selected.includes(pitch.id);
    if (!isSelected) {
      // Check if this podcast is flagged for a different week
      if (pitch.flaggedWeek && pitch.flaggedWeek !== weekKey) {
        // Check if there are podcasts flagged for this week that aren't selected
        const thisWeekUnselected = flaggedThisWeek.filter(p => !selected.includes(p.id));
        if (thisWeekUnselected.length > 0) {
          setSwapWarning({ adding: pitch, swapping: thisWeekUnselected });
          return;
        }
      }
      setSelected(prev => [...prev, pitch.id]);
    } else {
      setSelected(prev => prev.filter(id => id !== pitch.id));
    }
  };

  const handleSwapConfirm = async () => {
    if (!swapWarning) return;
    const { adding, swapping } = swapWarning;
    // Flag swapping ones for next week, add the new one
    try {
      for (const p of swapping) {
        await pitchesApi.unflag(p.id);
        // We'd need a "flag for next week" feature, for now just unflag
      }
      setSelected(prev => [...prev.filter(id => !swapping.map(p => p.id).includes(id)), adding.id]);
      toast(`Swapped. The previously flagged podcasts have been unflagged.`, 'info');
    } catch (e) { toast('Error swapping', 'error'); }
    setSwapWarning(null);
  };

  const handlePreview = async () => {
    if (!selected.length) { toast('Select at least one podcast', 'error'); return; }
    setLoading(true);
    try {
      const res = await emailApi.previewOpportunity(client.id, selected);
      setPreview(res.data.html);
    } catch (e) { toast('Preview failed: ' + (e.response?.data?.error || e.message), 'error'); }
    finally { setLoading(false); }
  };

  const handleCreateDraft = async () => {
    setCreating(true);
    try {
      const res = await emailApi.createOpportunityDraft(client.id, selected, sendFromEmail || null);
      toast(`Draft created in Gmail ✓ — "${res.data.subject}"`, 'success');
      onDraftCreated();
      onClose();
    } catch (e) { toast('Failed: ' + (e.response?.data?.error || e.message), 'error'); }
    finally { setCreating(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg" style={{ maxWidth: 800 }}>
        <div className="modal-header">
          <div>
            <h2>📧 Send Podcasts — {client.name}</h2>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>Select which podcasts to include in this week's email</div>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>

        {/* Swap warning */}
        {swapWarning && (
          <div style={{ background: 'var(--amber-soft)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 'var(--radius-sm)', padding: '12px 16px', marginBottom: 16 }}>
            <div style={{ fontWeight: 600, color: 'var(--amber)', marginBottom: 8, fontSize: 13 }}>
              ⚠ "{swapWarning.adding.podcastName}" was flagged for a different week.
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
              The following were flagged for this week: <strong>{swapWarning.swapping.map(p => p.podcastName).join(', ')}</strong>.<br />
              Would you like to swap them? (The flagged ones will be unflagged)
            </div>
            <div className="flex-center gap-2">
              <button className="btn btn-secondary btn-sm" onClick={() => { setSelected(p => [...p, swapWarning.adding.id]); setSwapWarning(null); }}>Add Anyway (Keep Both)</button>
              <button className="btn btn-primary btn-sm" onClick={handleSwapConfirm}>Swap</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setSwapWarning(null)}>Cancel</button>
            </div>
          </div>
        )}

        {!preview ? (
          <>
            {/* Podcast selection */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20, maxHeight: 380, overflowY: 'auto' }}>
              {sortedPool.map(pitch => {
                const isSelected = selected.includes(pitch.id);
                const isThisWeek = pitch.flaggedWeek === weekKey;
                const isOtherWeek = pitch.flaggedWeek && !isThisWeek;
                return (
                  <div key={pitch.id}
                    onClick={() => toggleSelect(pitch)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '12px 14px', borderRadius: 'var(--radius-sm)',
                      border: `1px solid ${isSelected ? 'var(--accent)' : 'var(--border)'}`,
                      background: isSelected ? 'var(--accent-soft)' : isThisWeek ? 'var(--green-soft)' : 'var(--bg-elevated)',
                      cursor: 'pointer', transition: 'all 0.15s'
                    }}>
                    <div style={{
                      width: 20, height: 20, borderRadius: 4, flexShrink: 0,
                      border: `2px solid ${isSelected ? 'var(--accent)' : 'var(--border)'}`,
                      background: isSelected ? 'var(--accent)' : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontSize: 12
                    }}>{isSelected ? '✓' : ''}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{pitch.podcastName}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                        {pitch.hostName}{pitch.monthlyListeners && ` · ${pitch.monthlyListeners}`}
                      </div>
                    </div>
                    {isThisWeek && <span className="badge badge-green">This Week</span>}
                    {isOtherWeek && <span className="badge badge-purple">{pitch.flaggedWeek}</span>}
                  </div>
                );
              })}
            </div>

            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
              {selected.length} podcast{selected.length !== 1 ? 's' : ''} selected
            </div>

            {googleAccounts.length > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Send from:</span>
                <select value={sendFromEmail} onChange={e => setSendFromEmail(e.target.value)} style={{ fontSize: 12, padding: '4px 8px', width: 'auto' }}>
                  {googleAccounts.map(a => <option key={a.email} value={a.email}>{a.email}</option>)}
                </select>
              </div>
            )}

            <div className="flex-center gap-2" style={{ justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button className="btn btn-secondary" onClick={handlePreview} disabled={!selected.length || loading}>
                {loading ? 'Loading...' : '👁 Preview Email'}
              </button>
              <button className="btn btn-primary" onClick={handleCreateDraft} disabled={!selected.length || creating}>
                {creating ? 'Creating...' : '✉ Create Gmail Draft'}
              </button>
            </div>
          </>
        ) : (
          <>
            <iframe
              srcDoc={preview}
              style={{ width: '100%', height: 480, border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', marginBottom: 16, background: '#fff' }}
              title="Email Preview"
            />
            <div className="flex-center gap-2" style={{ justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setPreview(null)}>← Edit Selection</button>
              <button className="btn btn-primary" onClick={handleCreateDraft} disabled={creating}>
                {creating ? 'Creating...' : '✉ Create Gmail Draft'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── EOW Modal ────────────────────────────────────────────────────────────────
function EOWModal({ clientData, onClose, onDraftCreated }) {
  const toast = useToast();
  const { client, sentPitches } = clientData;
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [googleAccounts, setGoogleAccounts] = useState([]);
  const [sendFromEmail, setSendFromEmail] = useState(client.googleAccountEmail || '');
  const isFused = sentPitches.length === 0; // no opportunities sent yet = fuse on Friday

  useEffect(() => {
    googleApi.getStatus().then(r => {
      const accounts = r.data.accounts || [];
      setGoogleAccounts(accounts);
      if (!sendFromEmail && accounts.length > 0) setSendFromEmail(client.googleAccountEmail || accounts[0].email || '');
    });
  }, []);

  const fusedPitchIds = isFused ? (clientData.flaggedThisWeek || []).map(p => p.id) : [];

  const handlePreview = async () => {
    setLoading(true);
    try {
      const res = await emailApi.previewEOW(client.id, fusedPitchIds);
      setPreview(res.data.html);
    } catch (e) { toast('Preview failed: ' + (e.response?.data?.error || e.message), 'error'); }
    finally { setLoading(false); }
  };

  const handleCreateDraft = async () => {
    setCreating(true);
    try {
      const res = await emailApi.createEOWDraft(client.id, fusedPitchIds, sendFromEmail || null);
      toast(`EOW draft created ✓ — "${res.data.subject}"`, 'success');
      onDraftCreated();
      onClose();
    } catch (e) { toast('Failed: ' + (e.response?.data?.error || e.message), 'error'); }
    finally { setCreating(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg" style={{ maxWidth: 800 }}>
        <div className="modal-header">
          <div>
            <h2>📊 End of Week Report — {client.name}</h2>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>
              {sentPitches.length > 0
                ? `Standalone report (${sentPitches.length} podcast${sentPitches.length > 1 ? 's' : ''} already sent this week)`
                : `Will check if there are unsent opportunities to fuse`}
            </div>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>

        {!preview ? (
          <>
            {googleAccounts.length > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Send from:</span>
                <select value={sendFromEmail} onChange={e => setSendFromEmail(e.target.value)} style={{ fontSize: 12, padding: '4px 8px', width: 'auto' }}>
                  {googleAccounts.map(a => <option key={a.email} value={a.email}>{a.email}</option>)}
                </select>
              </div>
            )}
            <div className="flex-center gap-2" style={{ justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button className="btn btn-secondary" onClick={handlePreview} disabled={loading}>
                {loading ? 'Loading...' : '👁 Preview Email'}
              </button>
              <button className="btn btn-primary" onClick={handleCreateDraft} disabled={creating}>
                {creating ? 'Creating...' : '✉ Create Gmail Draft'}
              </button>
            </div>
          </>
        ) : (
          <>
            <iframe
              srcDoc={preview}
              style={{ width: '100%', height: 480, border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', marginBottom: 16, background: '#fff' }}
              title="Email Preview"
            />
            <div className="flex-center gap-2" style={{ justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setPreview(null)}>← Back</button>
              <button className="btn btn-primary" onClick={handleCreateDraft} disabled={creating}>
                {creating ? 'Creating...' : '✉ Create Gmail Draft'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function SendWeekly() {
  const toast = useToast();
  const [weeklyStatus, setWeeklyStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendModal, setSendModal] = useState(null);
  const [eowModal, setEowModal] = useState(null);

  const load = () => {
    setLoading(true);
    emailApi.weeklyStatus()
      .then(r => setWeeklyStatus(r.data))
      .catch(() => toast('Could not load weekly status', 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const getStatus = (item) => {
    const { opportunitiesSentThisWeek } = item;
    if (opportunitiesSentThisWeek > 0) return 'sent';
    return 'nothing';
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <h1>Send EOW / Podcasts</h1>
        <p>Weekly sending status for all clients</p>
      </div>

      {loading ? (
        <div className="empty-state"><p>Loading...</p></div>
      ) : weeklyStatus.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📧</div>
          <h3>No clients yet</h3>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {weeklyStatus.map(item => {
            const status = getStatus(item);
            return (
              <div key={item.client.id} className="card" style={{
                display: 'flex', alignItems: 'center', gap: 16,
                borderLeft: `3px solid ${status === 'sent' ? 'var(--green)' : 'var(--amber)'}`
              }}>
                {/* Avatar */}
                <div style={{
                  width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                  background: status === 'sent' ? 'var(--green-soft)' : 'var(--amber-soft)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 16,
                  color: status === 'sent' ? 'var(--green)' : 'var(--amber)'
                }}>
                  {item.client.name.charAt(0).toUpperCase()}
                </div>

                {/* Name + status */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{item.client.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                    {status === 'sent'
                      ? `${item.opportunitiesSentThisWeek} podcast${item.opportunitiesSentThisWeek > 1 ? 's' : ''} sent this week`
                      : `${item.savedPoolCount} in saved pool${item.flaggedThisWeek.length > 0 ? ` · ${item.flaggedThisWeek.length} flagged for this week` : ''}`}
                  </div>
                </div>

                {/* Status badge */}
                <span className={`badge ${status === 'sent' ? 'badge-green' : 'badge-amber'}`}>
                  {status === 'sent' ? '✓ Podcasts Sent' : '⚠ Nothing Sent'}
                </span>

                {/* Action buttons */}
                <div className="flex-center gap-2">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => setSendModal(item)}
                    disabled={item.savedPoolCount === 0}
                    title={item.savedPoolCount === 0 ? 'No podcasts in saved pool' : ''}
                  >
                    {status === 'sent' ? '+ Send More Podcasts' : '📧 Send Podcasts'}
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setEowModal(item)}>
                    📊 Send EOW
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {sendModal && (
        <SendPodcastsModal
          clientData={sendModal}
          onClose={() => setSendModal(null)}
          onDraftCreated={load}
        />
      )}
      {eowModal && (
        <EOWModal
          clientData={eowModal}
          onClose={() => setEowModal(null)}
          onDraftCreated={load}
        />
      )}
    </div>
  );
}
